
import React, { useState } from 'react';
import { useRegionalGuidelines } from '../../hooks/useRegionalGuidelines';
import { useArchitectureStore } from '../../store/useArchitectureStore';
import { Map, ChevronUp, ChevronDown, Layers } from 'lucide-react';

const RegionalIdentityBar: React.FC = () => {
    const { regions } = useRegionalGuidelines();
    const { selectedRegionId, setSelectedRegionId, mode, selectedStyle, setSelectedStyle } = useArchitectureStore();
    const [isExpanded, setIsExpanded] = useState(false);
    const [isStyleExpanded, setIsStyleExpanded] = useState(false);

    if (mode !== 'elevation') return null;

    const selectedRegion = regions.find(r => r.id === selectedRegionId);

    const handleRegionSelect = (id: string) => {
        setSelectedRegionId(id);
        setIsExpanded(false);
    };

    return (
        <div className="absolute bottom-32 md:bottom-6 left-4 right-4 md:left-6 md:right-auto z-20 flex flex-col gap-3 pointer-events-none transition-all duration-300">
            {/* Container for vertical stack */}
            <div className="flex flex-row md:flex-col gap-2 md:gap-3 pointer-events-auto w-full md:w-64">

                {/* 1. Regional Selector */}
                <div className="relative flex-1">
                    {/* Expanded List (Upwards) */}
                    <div className={`
                        absolute bottom-full left-0 w-full mb-2
                        bg-stone-900/90 backdrop-blur-md border border-stone-800 rounded-xl overflow-hidden
                        transition-all duration-300 origin-bottom-left shadow-2xl
                        ${isExpanded ? 'opacity-100 scale-100 max-h-[400px] overflow-y-auto' : 'opacity-0 scale-95 max-h-0 pointer-events-none'}
                    `}>
                        <div className="p-2 grid grid-cols-1 gap-1">
                            {regions.map((region) => {
                                const isComplete = !!region.guideline_data?.voice_instruction;
                                return (
                                    <button
                                        key={region.id}
                                        onClick={() => handleRegionSelect(region.id)}
                                        disabled={!isComplete}
                                        className={`
                                            text-left p-3 rounded-lg transition-colors border
                                            ${!isComplete
                                                ? 'opacity-50 cursor-not-allowed border-transparent bg-stone-900/50'
                                                : selectedRegionId === region.id
                                                    ? 'bg-amber-900/40 border-amber-600/30 text-amber-100'
                                                    : 'border-transparent hover:bg-stone-800 text-stone-300'
                                            }
                                        `}
                                    >
                                        <div className="flex justify-between items-center">
                                            <div className="text-xs font-bold">{region.name}</div>
                                            {!isComplete && (
                                                <span className="text-[9px] uppercase tracking-wide bg-stone-800 text-stone-400 px-1.5 py-0.5 rounded">
                                                    Coming Soon
                                                </span>
                                            )}
                                        </div>
                                        {isComplete && (
                                            <div className="text-[10px] text-stone-500 line-clamp-1 mt-1">{region.description}</div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Trigger Button */}
                    <button
                        onClick={() => { setIsExpanded(!isExpanded); setIsStyleExpanded(false); }}
                        className={`
                            w-full flex items-center justify-between p-3 rounded-xl border transition-all shadow-lg
                            ${isExpanded
                                ? 'bg-stone-800 border-amber-500/50 text-amber-100'
                                : 'bg-stone-900/80 border-stone-800 text-stone-300 hover:bg-stone-800'}
                        `}
                    >
                        <div className="flex items-center gap-3 overflow-hidden">
                            <div className="p-1.5 rounded-lg bg-stone-800 text-amber-500 shrink-0">
                                <Map size={16} />
                            </div>
                            <div className="flex flex-col items-start truncate">
                                <span className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Region</span>
                                <span className="text-xs font-bold truncate">{selectedRegion?.name || 'Select Region'}</span>
                            </div>
                        </div>
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>

                {/* 2. Style Selector */}
                <div className="relative flex-1">
                    {/* Expanded List (Upwards) */}
                    <div className={`
                        absolute bottom-full left-0 w-full mb-2
                        bg-stone-900/90 backdrop-blur-md border border-stone-800 rounded-xl overflow-hidden
                        transition-all duration-300 origin-bottom-left shadow-2xl
                        ${isStyleExpanded ? 'opacity-100 scale-100 max-h-[300px] overflow-y-auto' : 'opacity-0 scale-95 max-h-0 pointer-events-none'}
                    `}>
                        <div className="p-2 grid grid-cols-1 gap-1">
                            {(['Traditional', 'Transitional', 'Contemporary'] as const).map((style) => (
                                <button
                                    key={style}
                                    onClick={() => {
                                        setSelectedStyle(style);
                                        setIsStyleExpanded(false);
                                    }}
                                    className={`
                                        text-left p-3 rounded-lg transition-colors border flex items-center gap-2
                                        ${selectedStyle === style
                                            ? 'bg-amber-900/40 border-amber-600/30 text-amber-100'
                                            : 'border-transparent hover:bg-stone-800 text-stone-300'}
                                    `}
                                >
                                    <Layers size={14} className={selectedStyle === style ? 'text-amber-500' : 'text-stone-600'} />
                                    <div className="text-xs font-bold">{style}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Trigger Button */}
                    <button
                        onClick={() => { setIsStyleExpanded(!isStyleExpanded); setIsExpanded(false); }}
                        className={`
                            w-full flex items-center justify-between p-3 rounded-xl border transition-all shadow-lg
                            ${isStyleExpanded
                                ? 'bg-stone-800 border-amber-500/50 text-amber-100'
                                : 'bg-stone-900/80 border-stone-800 text-stone-300 hover:bg-stone-800'}
                        `}
                    >
                        <div className="flex items-center gap-3 overflow-hidden">
                            <div className="p-1.5 rounded-lg bg-stone-800 text-amber-500 shrink-0">
                                <Layers size={16} />
                            </div>
                            <div className="flex flex-col items-start truncate">
                                <span className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Style</span>
                                <span className="text-xs font-bold truncate">{selectedStyle}</span>
                            </div>
                        </div>
                        {isStyleExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default RegionalIdentityBar;
